import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Users, UserCheck, UserX, Clock, CheckCircle, XCircle, 
  FileText, Upload, Mail, Phone, MapPin, Building2, Eye, Download 
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetails, setShowClientDetails] = useState(false);

  // Quote management
  const [quotes, setQuotes] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    quote_id: '',
    amount: '',
    valid_until: '',
    notes: ''
  });

  const handleBack = () => {
    window.location.href = '/dashboard';
  };

  useEffect(() => {
    loadClients();
    loadQuotes();
  }, []);

  useEffect(() => {
    if (filterStatus === 'all') {
      setFilteredClients(clients);
    } else {
      setFilteredClients(clients.filter(c => c.status === filterStatus));
    }
  }, [filterStatus, clients]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/list`);
      setClients(response.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const loadQuotes = async () => {
    try {
      const response = await axios.get(`${API}/quotes/list`);
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Error loading quotes:', error);
    }
  };

  const loadClientDetails = async (clientId) => {
    try {
      const response = await axios.get(`${API}/clients/${clientId}/profile`);
      setSelectedClient(response.data);
      setShowClientDetails(true);
    } catch (error) {
      toast.error("Erreur lors du chargement des détails");
      console.error(error);
    }
  };

  const validateClient = async (clientId, approved, notes = '') => {
    try {
      await axios.post(`${API}/clients/${clientId}/validate`, {
        approved,
        notes
      });

      toast.success(approved ? "Client approuvé" : "Client rejeté");
      loadClients();
      setShowClientDetails(false);
    } catch (error) {
      toast.error("Erreur lors de la validation");
      console.error(error);
    }
  };

  const sendQuote = async () => {
    if (!quoteForm.quote_id || !quoteForm.amount || !quoteForm.valid_until) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      await axios.post(`${API}/quotes/${quoteForm.quote_id}/respond`, {
        amount: parseFloat(quoteForm.amount),
        valid_until: new Date(quoteForm.valid_until).toISOString(),
        notes: quoteForm.notes
      });

      toast.success("Devis envoyé !");
      setQuoteForm({ quote_id: '', amount: '', valid_until: '', notes: '' });
      loadQuotes();
    } catch (error) {
      toast.error("Erreur lors de l'envoi du devis");
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      active: 'bg-blue-100 text-blue-800',
      suspended: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'pending') return <Clock className="h-4 w-4" />;
    if (status === 'approved' || status === 'active') return <CheckCircle className="h-4 w-4" />;
    if (status === 'rejected' || status === 'suspended') return <XCircle className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  const getEstablishmentTypeLabel = (type) => {
    const labels = {
      nightclub: 'Discothèque',
      bar: 'Bar',
      concert_hall: 'Salle de concert',
      restaurant: 'Restaurant',
      lounge: 'Lounge',
      festival: 'Festival',
      other: 'Autre'
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      kbis: 'KBIS',
      rib: 'RIB',
      tva: 'N° TVA',
      siret: 'SIRET',
      insurance: 'Assurance',
      impact_study: 'Étude d\'impact',
      floor_plan: 'Plan',
      sound_system: 'Système sono',
      other_admin: 'Autre'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header with back button */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleBack}
                className="flex items-center space-x-2"
              >
                <span>←</span>
                <span>Retour au Dashboard</span>
              </Button>
              <div className="border-l h-8"></div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Gestion des Clients</h1>
                <p className="text-slate-600 text-sm">Administration T-Kalé Pro-Acoustics</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="clients" className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="clients">👥 Gestion des Clients</TabsTrigger>
          <TabsTrigger value="quotes">💰 Demandes de Devis</TabsTrigger>
        </TabsList>

        {/* CLIENTS TAB */}
        <TabsContent value="clients" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-teal-600">{clients.length}</div>
                  <div className="text-sm text-slate-600">Total</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-600">
                    {clients.filter(c => c.status === 'pending').length}
                  </div>
                  <div className="text-sm text-slate-600">En attente</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">
                    {clients.filter(c => c.status === 'approved' || c.status === 'active').length}
                  </div>
                  <div className="text-sm text-slate-600">Approuvés</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600">
                    {clients.filter(c => c.status === 'active').length}
                  </div>
                  <div className="text-sm text-slate-600">Actifs</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-4">
                <Label>Filtrer par statut :</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="approved">Approuvés</SelectItem>
                    <SelectItem value="active">Actifs</SelectItem>
                    <SelectItem value="suspended">Suspendus</SelectItem>
                    <SelectItem value="rejected">Rejetés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Clients List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Liste des clients ({filteredClients.length})</span>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => window.open(`${API}/clients/export-excel`, '_blank')}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Excel
                  </Button>
                  <Button variant="outline" size="sm" onClick={loadClients}>
                    Actualiser
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-slate-500">Chargement...</div>
              ) : filteredClients.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucun client</div>
              ) : (
                <div className="space-y-3">
                  {filteredClients.map((client) => (
                    <div 
                      key={client.client_id} 
                      className="p-4 bg-slate-50 rounded-lg border hover:border-teal-500 transition-colors cursor-pointer"
                      onClick={() => loadClientDetails(client.client_id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-medium text-lg">{client.company_name}</h4>
                            <Badge className={getStatusColor(client.status)}>
                              {getStatusIcon(client.status)}
                              <span className="ml-1">{client.status}</span>
                            </Badge>
                            {client.admin_documents_complete && (
                              <Badge variant="outline" className="bg-green-50 text-green-700">
                                <FileText className="h-3 w-3 mr-1" />
                                Docs complets
                              </Badge>
                            )}
                          </div>
                          
                          <div className="text-sm text-slate-600 space-y-1">
                            <div className="flex items-center space-x-2">
                              <Building2 className="h-4 w-4" />
                              <span>{getEstablishmentTypeLabel(client.establishment_type)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Mail className="h-4 w-4" />
                              <span>{client.email}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Phone className="h-4 w-4" />
                              <span>{client.phone}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4" />
                              <span>{client.address}, {client.postal_code} {client.city}</span>
                            </div>
                          </div>
                          
                          <div className="mt-2 text-xs text-slate-500">
                            Inscrit le {new Date(client.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>

                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* QUOTES TAB */}
        <TabsContent value="quotes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Demandes de devis ({quotes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Aucune demande de devis</div>
              ) : (
                <div className="space-y-4">
                  {quotes.map((quote) => (
                    <div key={quote.quote_id} className="p-4 bg-slate-50 rounded-lg border">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-medium">{quote.service_type}</h4>
                            <Badge className={
                              quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              quote.status === 'sent' ? 'bg-blue-100 text-blue-800' :
                              quote.status === 'accepted' ? 'bg-green-100 text-green-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {quote.status}
                            </Badge>
                            <Badge variant="outline">{quote.urgency}</Badge>
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-2">{quote.description}</p>
                          
                          {quote.budget_range && (
                            <p className="text-sm text-slate-500">Budget indicatif: {quote.budget_range}</p>
                          )}
                          
                          <div className="text-xs text-slate-500 mt-2">
                            Demandé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                          </div>

                          {quote.quote_amount && (
                            <div className="mt-2 p-2 bg-green-50 rounded">
                              <span className="font-medium">Devis envoyé: </span>
                              <span className="text-lg font-bold text-green-700">{quote.quote_amount} €</span>
                              {quote.quote_valid_until && (
                                <span className="text-sm text-slate-600 ml-2">
                                  (valide jusqu'au {new Date(quote.quote_valid_until).toLocaleDateString('fr-FR')})
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {quote.status === 'pending' && (
                          <Button
                            size="sm"
                            onClick={() => setQuoteForm({ ...quoteForm, quote_id: quote.quote_id })}
                          >
                            Répondre
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quote Response Form */}
          {quoteForm.quote_id && (
            <Card className="border-teal-500">
              <CardHeader>
                <CardTitle>Envoyer un devis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Montant du devis (€) *</Label>
                    <Input
                      type="number"
                      value={quoteForm.amount}
                      onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })}
                      placeholder="Ex: 5000"
                    />
                  </div>

                  <div>
                    <Label>Valable jusqu'au *</Label>
                    <Input
                      type="date"
                      value={quoteForm.valid_until}
                      onChange={(e) => setQuoteForm({ ...quoteForm, valid_until: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Notes complémentaires</Label>
                  <Textarea
                    value={quoteForm.notes}
                    onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                    placeholder="Détails de la prestation, conditions, etc."
                    rows={3}
                  />
                </div>

                <div className="flex space-x-2">
                  <Button onClick={sendQuote} className="bg-teal-600 hover:bg-teal-700">
                    Envoyer le devis
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setQuoteForm({ quote_id: '', amount: '', valid_until: '', notes: '' })}
                  >
                    Annuler
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Client Details Modal (simplified as alert for now) */}
      {showClientDetails && selectedClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{selectedClient.company_name}</span>
                <Button variant="ghost" size="sm" onClick={() => setShowClientDetails(false)}>
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Badge */}
              <Badge className={getStatusColor(selectedClient.status)}>
                {selectedClient.status}
              </Badge>

              {/* Company Info */}
              <div className="space-y-2">
                <h4 className="font-medium">Informations entreprise</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">Type:</span> {getEstablishmentTypeLabel(selectedClient.establishment_type)}</div>
                  <div><span className="text-slate-500">SIRET:</span> {selectedClient.siret || 'Non renseigné'}</div>
                  <div><span className="text-slate-500">TVA:</span> {selectedClient.tva_number || 'Non renseigné'}</div>
                  <div><span className="text-slate-500">Capacité:</span> {selectedClient.establishment_capacity || 'Non renseigné'}</div>
                </div>
              </div>

              {/* Problem Description */}
              <div>
                <h4 className="font-medium mb-2">Problématique</h4>
                <p className="text-sm text-slate-600 p-3 bg-slate-50 rounded">
                  {selectedClient.problem_description}
                </p>
                {selectedClient.sound_system_info && (
                  <p className="text-sm text-slate-600 mt-2">
                    <span className="font-medium">Système sono:</span> {selectedClient.sound_system_info}
                  </p>
                )}
              </div>

              {/* Documents */}
              {selectedClient.documents && selectedClient.documents.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Documents téléchargés ({selectedClient.documents.length})</h4>
                  <div className="space-y-2">
                    {selectedClient.documents.map((doc) => (
                      <div key={doc.document_id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                        <div className="flex items-center space-x-2">
                          <FileText className="h-4 w-4 text-teal-600" />
                          <span>{getDocumentTypeLabel(doc.document_type)}</span>
                          <span className="text-slate-500">- {doc.file_name}</span>
                        </div>
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {selectedClient.status === 'pending' && (
                <div className="flex space-x-2 pt-4 border-t">
                  <Button
                    onClick={() => validateClient(selectedClient.client_id, true)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    <UserCheck className="h-4 w-4 mr-2" />
                    Approuver
                  </Button>
                  <Button
                    onClick={() => validateClient(selectedClient.client_id, false)}
                    variant="outline"
                    className="flex-1 text-red-600 border-red-600"
                  >
                    <UserX className="h-4 w-4 mr-2" />
                    Rejeter
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </div>
  );
};

export default ClientManagement;
